import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Space, Tag, Button, Modal, Tooltip, message } from 'antd';
import { TagOutlined, AppstoreOutlined, PlusOutlined, MoreOutlined, RightOutlined, CloseOutlined, BellOutlined, BellFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { categoryStorage } from '../../utils/storage';
import auctionService from '../../services/auctionService';
import notificationService from '../../services/notificationService';
import { isPushSupported, subscribeToPush } from '../../utils/pushNotifications';
import './CategorySection.css';

const { Title, Text } = Typography;

const CategorySection = ({
    categories = [],
    selectedCategory = 'all',
    onCategoryChange,
    loading = false,
    showAllCategories = false, // Prop to show all categories (used in category-results page)
    showViewAllButton = true // Prop to show/hide the "View All Categories" button
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [visibleCategoriesCount, setVisibleCategoriesCount] = useState(10); // Show first 10 categories initially
    const [localCategories, setLocalCategories] = useState([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [subscribedIds, setSubscribedIds] = useState([]);
    const [togglingId, setTogglingId] = useState(null);
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);

    useEffect(() => {
        if (currentUser) {
            notificationService.getSubscribedCategoryIds()
                .then(setSubscribedIds)
                .catch((error) => console.error('Error loading subscribed categories:', error));
        }
    }, [currentUser]);

    const handleToggleNotify = async (e, categoryId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentUser) {
            message.warning('Please login to get notified about auctions');
            navigate('/login');
            return;
        }

        const willSubscribe = !subscribedIds.includes(categoryId);

        setTogglingId(categoryId);
        try {
            if (willSubscribe && isPushSupported()) {
                try {
                    await subscribeToPush();
                } catch (pushError) {
                    console.error('Could not enable browser notifications:', pushError);
                }
            }

            const result = await notificationService.toggleCategorySubscription(categoryId);
            setSubscribedIds((prev) =>
                result.is_subscribed ? [...prev, categoryId] : prev.filter((id) => id !== categoryId)
            );
            message.success(result.message);
        } catch (error) {
            console.error('Error toggling category subscription:', error);
            message.error('Failed to update notification subscription');
        } finally {
            setTogglingId(null);
        }
    };

    // Load categories from cache or API
    useEffect(() => {
        const loadCategories = async () => {
            // First, try to get cached categories
            const cachedCategories = categoryStorage.getCachedCategories();
            
            if (cachedCategories.length > 0) {
                setLocalCategories(cachedCategories);
                return;
            }

            // If no cached categories or cache is expired, fetch from API
            if (categories.length === 0) {
                setIsLoadingCategories(true);
                try {
                    const response = await auctionService.getCategories();
                    const fetchedCategories = response.data;
                    setLocalCategories(fetchedCategories);
                    categoryStorage.saveCategories(fetchedCategories);
                } catch (error) {
                    console.error('Error loading categories:', error);
                } finally {
                    setIsLoadingCategories(false);
                }
            } else {
                // Use provided categories and cache them
                setLocalCategories(categories);
                categoryStorage.saveCategories(categories);
            }
        };

        loadCategories();
    }, [categories]);

    // Use local categories if available, otherwise fall back to provided categories
    const displayCategories = localCategories.length > 0 ? localCategories : categories;

    const handleCategoryClick = (categoryName) => {
        if (onCategoryChange) {
            onCategoryChange(categoryName === 'all' ? 'all' : categoryName.toLowerCase());
        }
        
        // Navigate to category results page
        const searchParams = new URLSearchParams();
        if (categoryName !== 'all') {
            searchParams.set('category', categoryName.toLowerCase());
        }
        
        navigate({
            pathname: '/category-results',
            search: searchParams.toString()
        });
        
        setIsModalVisible(false); // Close modal when category is selected
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };

    const visibleCategories = showAllCategories 
        ? displayCategories 
        : displayCategories.slice(0, visibleCategoriesCount);
    const hasMoreCategories = !showAllCategories && displayCategories.length > visibleCategoriesCount;

    return (
        <div className="category-section">
            <div style={{ 
                marginBottom: 24,
                padding: window.innerWidth <= 768 ? '16px' : '24px',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                borderRadius: '16px',
                boxShadow: '0 8px 24px rgba(82, 196, 26, 0.2)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background decoration */}
                <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(82, 196, 26, 0.1) 0%, transparent 70%)',
                    animation: 'float 6s ease-in-out infinite'
                }} />
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        flexWrap: 'wrap', 
                        gap: 12,
                        marginBottom: 8
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 768 ? 8 : 12 }}>
                        <TagOutlined style={{ 
                            fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
                            color: 'white',
                            flexShrink: 0
                        }} />
                        <Title level={3} style={{ 
                            margin: 0, 
                            color: 'white',
                            fontWeight: 800,
                            fontSize: window.innerWidth <= 576 ? '18px' : window.innerWidth <= 768 ? '20px' : '24px',
                            lineHeight: 1.2
                        }}>
                            Browse by Category
                        </Title>
                        </div>
                        {showViewAllButton && (
                            <Button
                                type="primary"
                                icon={<RightOutlined />}
                                onClick={() => navigate('/category-results')}
                                style={{
                                    fontSize: window.innerWidth <= 576 ? '12px' : window.innerWidth <= 768 ? '13px' : '14px',
                                    fontWeight: 600,
                                    height: window.innerWidth <= 576 ? '36px' : window.innerWidth <= 768 ? '38px' : '40px',
                                    padding: window.innerWidth <= 576 ? '0 16px' : '0 20px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)',
                                    transition: 'all 0.3s ease',
                                    color: 'white',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(31, 41, 55, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(31, 41, 55, 0.3)';
                                }}
                            >
                                View All Categories
                            </Button>
                        )}
                    </div>
                    <p style={{ 
                        fontSize: window.innerWidth <= 576 ? '13px' : window.innerWidth <= 768 ? '14px' : '16px',
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontWeight: 500,
                        lineHeight: 1.5,
                        margin: 0
                    }}>
                        Explore auctions by category to find exactly what you're looking for
                    </p>
                </div>
            </div>
            
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-10px) rotate(180deg); }
                }
            `}</style>

            <Row gutter={[10, 10]}>
                {/* All Categories Card */}
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card
                        hoverable
                        className={`category-card ${selectedCategory === 'all' ? 'selected' : ''}`}
                        onClick={() => handleCategoryClick('all')}
                        style={{
                            textAlign: 'center',
                            borderRadius: '12px',
                            border: selectedCategory === 'all' ? '2px solid #52c41a' : '2px solid #e8eaed',
                            boxShadow: selectedCategory === 'all' ? '0 8px 24px rgba(82, 196, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            height: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🗂️</div>
                        <div style={{ 
                            fontSize: '14px',
                            fontWeight: 600,
                            color: selectedCategory === 'all' ? '#52c41a' : '#374151'
                        }}>
                            All Categories
                        </div>
                    </Card>
                </Col>

                {/* Category Cards */}
                {visibleCategories.map(category => (
                    <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={4} key={category.id}>
                        <Card
                            hoverable
                            className={`category-card ${selectedCategory === category.name.toLowerCase() ? 'selected' : ''}`}
                            onClick={() => handleCategoryClick(category.name)}
                            style={{
                                textAlign: 'center',
                                borderRadius: '12px',
                                border: selectedCategory === category.name.toLowerCase() ? '2px solid #52c41a' : '2px solid #e8eaed',
                                boxShadow: selectedCategory === category.name.toLowerCase() ? '0 8px 24px rgba(82, 196, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative'
                            }}
                        >
                            <Tooltip title={subscribedIds.includes(category.id) ? 'Stop notifying me' : 'Notify me about new auctions'}>
                                <Button
                                    type="text"
                                    shape="circle"
                                    size="small"
                                    loading={togglingId === category.id}
                                    icon={subscribedIds.includes(category.id) ? <BellFilled style={{ color: '#52c41a' }} /> : <BellOutlined style={{ color: '#8c8c8c' }} />}
                                    onClick={(e) => handleToggleNotify(e, category.id)}
                                    style={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}
                                />
                            </Tooltip>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                {category.icon || '📦'}
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: selectedCategory === category.name.toLowerCase() ? '#52c41a' : '#374151'
                            }}>
                                {category.name}
                            </div>
                        </Card>
                    </Col>
                ))}

                {/* Show More Button - Navigate to category-results */}
                {hasMoreCategories && (
                    <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={4}>
                        <Card
                            hoverable
                            className="show-more-card"
                            onClick={() => navigate('/category-results')}
                            style={{
                                textAlign: 'center',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                zIndex: 1,
                                border: '2px solid #52c41a',
                                background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(82, 196, 26, 0.3)';
                                e.currentTarget.style.borderColor = '#389e0d';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                e.currentTarget.style.borderColor = '#52c41a';
                            }}
                        >
                            <RightOutlined style={{ 
                                fontSize: '32px', 
                                marginBottom: '8px',
                                color: '#52c41a',
                                position: 'relative',
                                zIndex: 2
                            }} />
                            <div style={{ 
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#52c41a',
                                position: 'relative',
                                zIndex: 2
                            }}>
                                View All
                            </div>
                            <div style={{ 
                                fontSize: '12px',
                                color: '#8c8c8c',
                                marginTop: '4px',
                                position: 'relative',
                                zIndex: 2
                            }}>
                                +{displayCategories.length - visibleCategoriesCount} more
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>

            

            {/* Categories Modal */}
            <Modal
                title={
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        padding: '8px 0'
                    }}>
                        <div style={{
                            padding: '8px',
                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <TagOutlined style={{ 
                                color: 'white', 
                                fontSize: '20px' 
                            }} />
                        </div>
                        <div>
                            <Title level={4} style={{ 
                                margin: 0, 
                                color: '#374151',
                                fontWeight: 700
                            }}>
                                Browse All Categories
                            </Title>
                            <p style={{ 
                                fontSize: '14px',
                                color: '#6b7280',
                                marginTop: '2px',
                                margin: 0
                            }}>
                                Select a category to explore auctions
                            </p>
                        </div>
                    </div>
                }
                open={isModalVisible}
                onCancel={handleModalCancel}
                footer={null}
                width={1200}
                style={{ top: 20 }}
                className="categories-modal"
                styles={{
                    body: {
                        padding: '16px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
                    }
                }}
            >
                <div style={{ 
                    maxHeight: '65vh', 
                    overflowY: 'auto',
                    paddingRight: '8px'
                }}>
                    <Row gutter={[10, 10]}>
                        {/* All Categories Card in Modal */}
                        <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={4}>
                            <Card
                                hoverable
                                className={`category-card ${selectedCategory === 'all' ? 'selected' : ''}`}
                                onClick={() => handleCategoryClick('all')}
                                style={{
                                    textAlign: 'center',
                                    borderRadius: '12px',
                                    border: selectedCategory === 'all' ? '2px solid #52c41a' : '2px solid #e8eaed',
                                    boxShadow: selectedCategory === 'all' ? '0 8px 24px rgba(82, 196, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    height: '120px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🗂️</div>
                                <div style={{ 
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: selectedCategory === 'all' ? '#52c41a' : '#374151'
                                }}>
                                    All Categories
                                </div>
                            </Card>
                        </Col>

                        {/* All Category Cards in Modal */}
                        {displayCategories.map(category => (
                            <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={4} key={category.id}>
                                <Card
                                    hoverable
                                    className={`category-card ${selectedCategory === category.name.toLowerCase() ? 'selected' : ''}`}
                                    onClick={() => handleCategoryClick(category.name)}
                                    style={{
                                        textAlign: 'center',
                                        borderRadius: '12px',
                                        border: selectedCategory === category.name.toLowerCase() ? '2px solid #52c41a' : '2px solid #e8eaed',
                                        boxShadow: selectedCategory === category.name.toLowerCase() ? '0 8px 24px rgba(82, 196, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        height: '120px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    <Tooltip title={subscribedIds.includes(category.id) ? 'Stop notifying me' : 'Notify me about new auctions'}>
                                        <Button
                                            type="text"
                                            shape="circle"
                                            size="small"
                                            loading={togglingId === category.id}
                                            icon={subscribedIds.includes(category.id) ? <BellFilled style={{ color: '#52c41a' }} /> : <BellOutlined style={{ color: '#8c8c8c' }} />}
                                            onClick={(e) => handleToggleNotify(e, category.id)}
                                            style={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}
                                        />
                                    </Tooltip>
                                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                        {category.icon || '📦'}
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: selectedCategory === category.name.toLowerCase() ? '#52c41a' : '#374151'
                                    }}>
                                        {category.name}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </Modal>
        </div>
    );
};

export default CategorySection;
